import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import download from 'downloadjs';
import 'yet-another-react-lightbox/styles.css';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import Empty from 'src/components/Empty';
import ContentBlock from 'src/components/ContentBlock';
import Search from 'src/components/Search/Search';
import { GenericDialogue } from 'src/components/Dialogue/Dialogue';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import DocumentsRepeater from 'src/components/Documents/DocumentsRepeater/DocumentsRepeater';
import GroupedDocumentsList from 'src/components/Documents/GroupedDocumentsList';
import DocumentsSkeletonGrid from 'src/components/Documents/skeletons/DocumentsSkeletonGrid';
import { filterDocumentsBySearchTerm } from 'src/components/DocumentsNewLocalState/helpers';
import ModernizedDocumentForm from 'src/pages/MagicLinkSignedOutServiceRequest/components/ModernizedDocumentForm/ModernizedDocumentForm';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { getUrlExtension } from 'src/helpers/CommonHelpers';
import { IntroContent, USER_ROLES } from 'src/common/constants';
import './Documents.scss';

interface AddDocumentButtonProps {
  labelName: string;
  onClick: () => void;
}

const AddDocumentButton = ({ labelName, onClick }: AddDocumentButtonProps) => (
  <Button
    color="teal"
    label={`Add ${labelName}`}
    onClick={onClick}
    icon={<FontAwesomeIcon glyph="file-contract" weight="light" size="small" />}
    size="small"
  />
);

export interface DocumentsModernProps {
  documents: any[];
  user: any;
  labelName: string;
  saving?: boolean;
  loading?: boolean;
  onSave: (formData: DocumentFormData) => Promise<void> | void;
  onDeleteDocument: (document: any) => void;
  groupDocuments?: boolean;
  from?: string;
  useCustomConfirmation?: boolean;
  introContent?: boolean;
}

const DocumentsModern = ({
  documents,
  user,
  labelName,
  saving = false,
  loading = false,
  onSave,
  onDeleteDocument,
  groupDocuments = false,
  from,
  useCustomConfirmation,
  introContent,
}: DocumentsModernProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const isHomeOwnerOrOccupant: boolean =
    roleName === USER_ROLES.HOME_OWNER || roleName === USER_ROLES.OCCUPANT;

  const isRizeTheme: boolean = localStorage.getItem('theme') === 'Rize';

  const canShowHeaderAddButton: boolean = !(
    roleName === USER_ROLES.STRATA_MANAGER ||
    roleName === USER_ROLES.VENDOR ||
    roleName === USER_ROLES.OCCUPANT ||
    roleName === USER_ROLES.REALTOR ||
    (isHomeOwnerOrOccupant && isRizeTheme)
  );

  const canShowEmptyStateAddButton: boolean = !(
    roleName === USER_ROLES.STRATA_MANAGER ||
    roleName === USER_ROLES.HOME_OWNER ||
    roleName === USER_ROLES.REALTOR ||
    (isHomeOwnerOrOccupant && isRizeTheme)
  );

  const filteredDocuments: any[] = useMemo(
    () => filterDocumentsBySearchTerm(documents ?? [], searchTerm),
    [documents, searchTerm]
  );

  const isSearching: boolean = searchTerm.trim().length > 0;

  const handleDownload = (url: string): void => {
    if (url.includes('.mp4')) {
      toast.success(
        `Your ${labelName} download has started, It may take some time.`,
        { autoClose: 12000, position: 'top-center' }
      );
    }
    download(url);
  };

  const handleImageClick = (
    _index: number,
    url: string,
    isImage: boolean
  ): void => {
    if (!isImage) {
      window.open(url, '_blank')?.focus();
      return;
    }

    if (getUrlExtension(url) === 'heic') {
      window.open(url, '_blank')?.focus();
      return;
    }

    setSelectedImage(url);
    setLightboxOpen(true);
  };

  const handleSave = async (formData: DocumentFormData): Promise<void> => {
    await onSave(formData);
    setIsFormOpen(false);
  };

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const hasDocuments: boolean =
    Array.isArray(documents) && documents.length > 0;

  const shouldRenderGrouped: boolean = groupDocuments && !isSearching;

  return (
    <div className="documents">
      {introContent && (
        <ContentBlock content={IntroContent.manualsAndDocuments} />
      )}
      <div className="documents-header">
        <div className="documents-search">
          <Search
            size="small"
            placeholder={`${labelName}...`}
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value ?? '')}
            theme={undefined}
          />
        </div>
        {canShowHeaderAddButton && (
          <AddDocumentButton labelName={labelName} onClick={openForm} />
        )}
      </div>
      {loading ? (
        <DocumentsSkeletonGrid />
      ) : hasDocuments ? (
        shouldRenderGrouped ? (
          <GroupedDocumentsList
            documents={filteredDocuments}
            user={user}
            onDownload={handleDownload}
            onDeleteDocument={onDeleteDocument}
            onClick={handleImageClick}
            useCustomConfirmation={useCustomConfirmation}
          />
        ) : (
          <DocumentsRepeater
            documents={filteredDocuments}
            onDeleteDocument={onDeleteDocument}
            onDownload={handleDownload}
            onClick={handleImageClick}
            user={user}
            from={from}
            useCustomConfirmation={useCustomConfirmation}
          />
        )
      ) : (
        <Empty
          headline={`No ${labelName} yet`}
          strapline={`All ${labelName} you add will be listed here so it's easier for you to manage.`}
          searchIsActive={isSearching ? searchTerm : ''}
          cta={
            canShowEmptyStateAddButton ? (
              <div>
                <AddDocumentButton labelName={labelName} onClick={openForm} />
              </div>
            ) : null
          }
          image={
            <img
              alt={`No ${labelName} yet`}
              src="/assets/empty/documents.svg"
            />
          }
          direction={undefined}
        />
      )}
      <ReusableLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        image={selectedImage}
      />
      <GenericDialogue
        isOpen={isFormOpen}
        onClose={closeForm}
        title={`Add ${labelName}`}
        description={`Upload a document to attach to this ${labelName.toLowerCase()}.`}
        maxWidth="135rem"
      >
        <ModernizedDocumentForm
          saving={saving}
          onSave={handleSave}
          onClose={closeForm}
          from={from}
        />
      </GenericDialogue>
    </div>
  );
};

export default DocumentsModern;
